import {
  BillingProvider,
  ClaimsProcessor,
  HeadwayVerificationStatus,
  LineOfBusiness,
  ManualVerificationStatus,
  State,
} from "@prisma/client";

import {
  ALWAYS_OON_LINES_OF_BUSINESS,
  type EligibilityCheckStatus,
} from "@marble/core/modules/insurance/constants";
import type {
  InsuranceInfoCompletenessInput,
  InsuranceInfoForBillingRoute,
  InsuranceInfoForNetworkCheck,
  InsuranceVerificationStatuses,
  PayerNetworkStatusForCheck,
} from "@marble/core/modules/insurance/types";

/**
 * Mirror of the {@link NirvanaVerificationStatus} string values used by the
 * eligibility helpers. The full enum lives with the Nirvana client in
 * `apps/web/src/lib/clients/nirvana/constants.ts`; we duplicate the few values
 * actually consumed here so this package stays free of web-app imports.
 */
const NIRVANA_ELIGIBLE = "Eligible";
const NIRVANA_OON = "Out of Network";

/**
 * Whether claims for this insurance go through Headway. `claimsProcessor` is
 * the source of truth; older rows that predate it only have
 * `billingProvider`, so that is the fallback when the processor is unset.
 */
export const isBilledThroughHeadway = (
  insuranceInfo?: InsuranceInfoForBillingRoute | null,
): boolean =>
  insuranceInfo?.claimsProcessor === ClaimsProcessor.HEADWAY ||
  (insuranceInfo?.claimsProcessor == null &&
    insuranceInfo?.billingProvider === BillingProvider.HEADWAY);

export const insuranceInfoIsComplete = (
  insuranceInfo: InsuranceInfoCompletenessInput,
) => {
  return !!(
    insuranceInfo.insurancePayerId &&
    insuranceInfo.memberId &&
    insuranceInfo.memberDOBString &&
    // We don't need student address for headway
    (insuranceInfo.claimsProcessor === ClaimsProcessor.HEADWAY ||
      insuranceInfo.addressId)
  );
};

export const getInnPayerNetworkStatus = ({
  state,
  lineOfBusiness,
  payerNetworkStatuses,
}: {
  state: State;
  lineOfBusiness: LineOfBusiness | null | undefined;
  payerNetworkStatuses: PayerNetworkStatusForCheck[];
}) => {
  if (lineOfBusiness && ALWAYS_OON_LINES_OF_BUSINESS.has(lineOfBusiness)) {
    return undefined;
  }

  return payerNetworkStatuses.find(
    (status) =>
      status.state === state &&
      status.lineOfBusiness === lineOfBusiness &&
      status.isINN,
  );
};

export const getInnPayerNetworkStatusFromInsuranceInfo = (
  insuranceInfo: InsuranceInfoForNetworkCheck | undefined | null,
) => {
  if (!insuranceInfo) return null;
  if (!insuranceInfo.lineOfBusiness) return null;
  return (
    getInnPayerNetworkStatus({
      state: insuranceInfo.state,
      lineOfBusiness: insuranceInfo.lineOfBusiness,
      payerNetworkStatuses:
        insuranceInfo.InsurancePayer?.InsurancePayerNetworkStatus || [],
    }) ?? null
  );
};

export const getIsPayerLobINN = ({
  state,
  lineOfBusiness,
  payerNetworkStatuses,
  considerNotAcceptingNewClientsOON = false,
}: {
  state: State;
  lineOfBusiness: LineOfBusiness | null | undefined;
  payerNetworkStatuses: PayerNetworkStatusForCheck[];
  considerNotAcceptingNewClientsOON?: boolean;
}) => {
  if (!lineOfBusiness) return false;
  if (ALWAYS_OON_LINES_OF_BUSINESS.has(lineOfBusiness)) return false;

  const innIpns = getInnPayerNetworkStatus({
    state,
    lineOfBusiness,
    payerNetworkStatuses,
  });

  if (!innIpns) return false;

  if (considerNotAcceptingNewClientsOON) {
    return innIpns.acceptingNewClients;
  }

  return true;
};

/**
 * Check if insurance is explicitly OON (i.e. incomplete insurance returns false).
 *
 * Note: this considers not accepting new clients as OON.
 */
export const getIsInsuranceInfoOON = (
  insuranceInfo: InsuranceInfoForNetworkCheck | undefined | null,
) => {
  if (!insuranceInfo) return false;

  // Patient explicitly reported no insurance — treat as OON so the auto-
  // refer-out path fires. No payer/LOB is required (or expected) here.
  if (insuranceInfo.doesNotHaveInsurance) return true;

  if (!insuranceInfo.lineOfBusiness || !insuranceInfo.insurancePayerId) {
    return false;
  }

  return !getIsPayerLobINN({
    state: insuranceInfo.state,
    lineOfBusiness: insuranceInfo.lineOfBusiness,
    payerNetworkStatuses:
      insuranceInfo.InsurancePayer?.InsurancePayerNetworkStatus || [],
    considerNotAcceptingNewClientsOON: true,
  });
};

/**
 * True when insurance counts as out-of-network for an OON refer-out: the
 * payer/LOB is OON, or the latest Headway (or Nirvana) verification is OON.
 *
 * Families often pick an INN commercial payer in the portal (e.g. UHC) when
 * the real plan is Medicare Advantage. Headway then marks the verification
 * OON while the payer row still looks INN, so payer-network OON alone is not
 * enough. Shared by the refer-out dialog guard and the delayed refer-out
 * comms so they agree on what "OON" means.
 */
export const getIsInsuranceOONForReferOut = (
  insuranceInfo:
    | (InsuranceInfoForNetworkCheck & {
        InsuranceVerifications: Pick<
          InsuranceVerificationStatuses,
          "headwayVerificationStatus" | "nirvanaVerificationStatus"
        >[];
      })
    | null
    | undefined,
): boolean => {
  if (!insuranceInfo) {
    return false;
  }
  if (getIsInsuranceInfoOON(insuranceInfo)) {
    return true;
  }
  return _latestVerificationMarksOON(insuranceInfo.InsuranceVerifications[0]);
};

/**
 * Check if insurance is explicitly INN (i.e. incomplete insurance returns false).
 */
export const getIsInsuranceInfoINN = (
  insuranceInfo: InsuranceInfoForNetworkCheck | undefined | null,
  opts: {
    considerNotAcceptingNewClientsOON?: boolean;
  } = {},
) => {
  if (!insuranceInfo) return false;
  return getIsPayerLobINN({
    state: insuranceInfo.state,
    lineOfBusiness: insuranceInfo.lineOfBusiness,
    payerNetworkStatuses:
      insuranceInfo.InsurancePayer?.InsurancePayerNetworkStatus || [],
    considerNotAcceptingNewClientsOON: opts.considerNotAcceptingNewClientsOON,
  });
};

export const getEligibilityCheckStatus = (
  insuranceVerification: InsuranceVerificationStatuses,
  usePendingManualVerificationIfNotSet = false,
): EligibilityCheckStatus => {
  // If manual status set and not NOT_SUPPORTED, use that
  if (
    insuranceVerification.manualVerificationStatus &&
    insuranceVerification.manualVerificationStatus !==
      ManualVerificationStatus.NOT_SUPPORTED
  ) {
    return insuranceVerification.manualVerificationStatus ===
      ManualVerificationStatus.ELIGIBLE
      ? "Eligible"
      : "Ineligible";
  }

  if (insuranceVerification.nirvanaVerificationStatus === NIRVANA_ELIGIBLE) {
    return "Eligible";
  }

  if (insuranceVerification.nirvanaVerificationStatus === NIRVANA_OON) {
    return "Out of Network";
  }

  if (
    !insuranceVerification.manualVerificationStatus &&
    usePendingManualVerificationIfNotSet
  ) {
    return "Pending Manual Verification";
  }

  return "Ineligible";
};

export const getInsuranceVerificationIsPending = (
  insuranceVerification: InsuranceVerificationStatuses,
) => {
  // If we need to setup insurance with Headway, these statuses are considered
  // pending or needs action for Marble or the client, so we don't mistake the
  // insurance as ineligible and action on this client differently.
  if (
    insuranceVerification.headwayVerificationStatus &&
    (insuranceVerification.headwayVerificationStatus ===
      HeadwayVerificationStatus.NEEDS_SUBMISSION_TO_HEADWAY ||
      insuranceVerification.headwayVerificationStatus ===
        HeadwayVerificationStatus.AWAITING_CLIENT_HEADWAY_SETUP ||
      insuranceVerification.headwayVerificationStatus ===
        HeadwayVerificationStatus.HEADWAY_MANUAL_VERIFICATION)
  ) {
    return true;
  }

  return false;
};

export const getInsuranceVerificationIsEligible = (
  insuranceVerification?: InsuranceVerificationStatuses,
) => {
  // A manual denial supersedes any older positive vendor results.
  if (
    insuranceVerification?.manualVerificationStatus ===
    ManualVerificationStatus.INELIGIBLE
  ) {
    return false;
  }

  return (
    insuranceVerification?.manualVerificationStatus ===
      ManualVerificationStatus.ELIGIBLE ||
    insuranceVerification?.nirvanaVerificationStatus === NIRVANA_ELIGIBLE ||
    insuranceVerification?.headwayVerificationStatus ===
      HeadwayVerificationStatus.COMPLETE_ELIGBLE
  );
};

const _latestVerificationMarksOON = (
  latest:
    | Pick<
        InsuranceVerificationStatuses,
        "headwayVerificationStatus" | "nirvanaVerificationStatus"
      >
    | undefined,
): boolean => {
  if (!latest) {
    return false;
  }
  if (latest.headwayVerificationStatus === HeadwayVerificationStatus.OON) {
    return true;
  }
  // When Headway has a status, it is the source of truth for Headway payers.
  if (latest.headwayVerificationStatus != null) {
    return false;
  }
  return latest.nirvanaVerificationStatus === NIRVANA_OON;
};
